import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Dropdown, 
  Avatar, 
  Badge, 
  Space, 
  Typography, 
  Button, 
  Tooltip, 
  theme, 
  message,
  List,
  Skeleton,
  Tag,
  Popover
} from 'antd';
import { 
  BellOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  UserOutlined, 
  EyeOutlined,
  CheckOutlined,
  DeleteOutlined,
  SettingOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import config from '../config';
import './NotificationBell.css'; // We'll create this CSS file

const { Text, Title } = Typography;

const NotificationBell = ({ darkTheme }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);

  const {
    token: { 
      colorTextBase, 
      colorPrimary, 
      colorBorderSecondary, 
      colorSuccess, 
      colorWarning, 
      colorError,
      colorInfo,
      colorBgContainer,
      colorBgElevated,
      colorFillSecondary
    },
  } = theme.useToken();

  const getCurrentUserId = () => {
    // Replace with actual user ID retrieval
    return Cookies.get('userId') || 'current-user-id';
  };

  // Memoized fetch function
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/notifications`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const result = await response.json();
      
      if (result.success) {
        const sortedNotifications = (result.data || []).sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setNotifications(sortedNotifications);
        setNotificationCount(result.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      message.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (notificationId, event) => {
    if (event) event.stopPropagation();
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => 
            notif._id === notificationId ? { ...notif, read: true } : notif
          )
        );
        setNotificationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${config.apiBaseUrl}/api/notifications/${userId}/read-all`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, read: true }))
        );
        setNotificationCount(0);
        message.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      message.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId, event) => {
    if (event) event.stopPropagation();
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
        // Adjust unread count if notification was unread
        const notification = notifications.find(n => n._id === notificationId);
        if (notification && !notification.read) {
          setNotificationCount(prev => Math.max(0, prev - 1));
        }
        message.success('Notification deleted');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      message.error('Failed to delete notification');
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Real-time updates with WebSocket or polling
    const interval = setInterval(() => {
      if (!dropdownVisible) {
        fetchNotifications();
      }
    }, 60000); // Poll every minute
    
    return () => clearInterval(interval);
  }, [fetchNotifications, dropdownVisible]);

  const getNotificationConfig = (type) => {
    const configs = {
      success: {
        icon: <CheckCircleOutlined />,
        color: colorSuccess,
        bgColor: `${colorSuccess}15`,
        text: 'Success'
      },
      warning: {
        icon: <WarningOutlined />,
        color: colorWarning,
        bgColor: `${colorWarning}15`,
        text: 'Warning'
      },
      error: {
        icon: <ClockCircleOutlined />,
        color: colorError,
        bgColor: `${colorError}15`,
        text: 'Error'
      },
      info: {
        icon: <InfoCircleOutlined />,
        color: colorInfo || colorPrimary,
        bgColor: `${colorPrimary}15`,
        text: 'Info'
      }
    };
    
    return configs[type] || configs.info;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }
    
    if (notification.data?.url) {
      navigate(notification.data.url);
      setDropdownVisible(false);
    }
  };

  const renderNotificationItem = (notification) => {
    const config = getNotificationConfig(notification.type);
    
    return (
      <div 
        className={`notification-item ${!notification.read ? 'unread' : ''}`}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className="notification-icon-container">
          <div 
            className="notification-icon-wrapper"
            style={{ backgroundColor: config.bgColor }}
          >
            <span style={{ color: config.color, fontSize: '16px' }}>
              {config.icon}
            </span>
          </div>
        </div>
        
        <div className="notification-content">
          <div className="notification-header">
            <Space size={4} align="center">
              <Text strong style={{ fontSize: '13px' }}>
                {notification.title}
              </Text>
              <Tag 
                color={config.color}
                style={{ 
                  fontSize: '10px', 
                  padding: '0 4px', 
                  height: '16px',
                  lineHeight: '16px',
                  border: 'none'
                }}
              >
                {config.text}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              {formatTime(notification.createdAt)}
            </Text>
          </div>
          
          <Text 
            className="notification-message"
            type="secondary"
            style={{ fontSize: '12px', lineHeight: 1.4, margin: '4px 0' }}
          >
            {notification.message}
          </Text>
          
          <div className="notification-footer">
            <Space size={6} align="center">
              <UserOutlined style={{ fontSize: '11px', opacity: 0.6 }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {notification.userName || 'System'}
              </Text>
            </Space>
            
            <Space size={8} className="notification-actions">
              {!notification.read && (
                <Tooltip title="Mark as read">
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined style={{ fontSize: '12px' }} />}
                    onClick={(e) => markAsRead(notification._id, e)}
                  />
                </Tooltip>
              )}
              
              <Tooltip title="Delete">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
                  onClick={(e) => deleteNotification(notification._id, e)}
                />
              </Tooltip>
              
              {notification.data?.action && (
                <Button
                  type="link"
                  size="small"
                  style={{ fontSize: '11px', padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (notification.data.url) {
                      navigate(notification.data.url);
                      setDropdownVisible(false);
                    }
                  }}
                >
                  View Details
                </Button>
              )}
            </Space>
          </div>
        </div>
      </div>
    );
  };

  const dropdownContent = (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <div className="dropdown-header">
        <Title level={5} style={{ margin: 0 }}>Notifications</Title>
        <Space>
          {notificationCount > 0 && (
            <Button
              type="text"
              size="small"
              onClick={markAllAsRead}
              style={{ fontSize: '12px' }}
            >
              <CheckOutlined /> Mark all read
            </Button>
          )}         
        </Space>
      </div>
      
      {notificationCount > 0 && (
        <div className="unread-badge">
          <Badge 
            count={notificationCount} 
            size="small" 
            style={{ backgroundColor: colorPrimary }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {notificationCount} unread
          </Text>
        </div>
      )}
      
      <div className="notifications-list">
        {loading ? (
          <div className="loading-state">
            {[1, 2, 3].map(i => (
              <div key={i} className="notification-skeleton">
                <Skeleton avatar paragraph={{ rows: 2 }} active />
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <BellOutlined style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }} />
            <Text type="secondary">No notifications yet</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              We'll notify you when something arrives
            </Text>
          </div>
        ) : (
          <List
            dataSource={notifications.slice(0, 10)} // Show last 10 notifications
            renderItem={renderNotificationItem}
            locale={{ emptyText: 'No notifications' }}
          />
        )}
      </div>
      
      {notifications.length > 0 && (
        <div className="dropdown-footer">
          <Button
            type="text"
            block
            onClick={() => {
              navigate('/notifications');
              setDropdownVisible(false);
            }}
          >
            View All Notifications
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={dropdownContent}
      trigger="click"
      placement="bottomRight"
      open={dropdownVisible}
      onOpenChange={setDropdownVisible}
      overlayClassName="notification-popover"
      overlayStyle={{
        width: '380px',
        maxHeight: '500px',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Badge 
        count={notificationCount} 
        size="small" 
        offset={[-2, 2]}
        style={{ 
          backgroundColor: colorPrimary,
          boxShadow: `0 0 0 2px ${colorBgContainer}`
        }}
      >
        <Tooltip title="Notifications">
          <Button
            type="text"
            shape="circle"
            icon={<BellOutlined />}
            className={`notification-button ${dropdownVisible ? 'active' : ''}`}
            style={{
              fontSize: '18px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Tooltip>
      </Badge>
    </Popover>
  );
};

export default NotificationBell;